import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  setErrorSentKeys,
  setSuccessSentKeys
} from '../../redux/actions/rootAction';
import React from 'react';
import PaperflyAPI, {
  getResponseErrorMessage
} from '../../server/api/PaperflyAPI';
import {
  DOC_TYPE_INFORMATION,
  DOC_TYPE_IMMEDIATE_CONFIRMATION,
  DOC_TYPE_FORM,
  DOC_TYPE_PDF_FORM,
  isDocumentSubmitted,
  DOC_TYPE_DOCUMENT,
  DOC_TYPE_ID_CHECK,
  defineIdCheckDocumentTypeFromStatus,
  ID_CHECK_STEP_DOC_FRONT,
  ID_CHECK_STEP_DOC_BACK,
  ID_CHECK_STEP_DOC_SELFIE,
  collectPartialCompleteDocuments
} from '../helpers/utils';
import II18 from '../translate/II18';
import { gae, gaeError } from '../helpers/Analytics';
import {
  endJourney,
  JOURNEY,
  runInJourney,
  setJourneyAttributes,
  withSpan
} from '../../telemetry/tracing';
import { resetRequestIdCookie } from '../../lib/utils';
import { analyzeJpegOrPngBase64, getBase64FromUrl } from '../../lib/fileUtils';
import { esMapToJsonObject } from '../helpers/formUtils';
import { selectPreparedState } from '../../redux/selectors/preparedState';
import { isSendRequestActive } from '../../redux/sendRequestTask';

export const SUCCESSFUL_DOCUMENT_STATUSES = ['ACCEPTED', 'SUBMITTED'];

const isInvalidImageUploadError = message =>
  Boolean(
    message &&
      (message.indexOf('INVALID_JPEG_OR_PNG_BASE64') !== -1 ||
        message.indexOf('Error on fetch base64 from url') !== -1 ||
        message.indexOf('must be provided') !== -1 ||
        message.indexOf('not a valid jpeg or png') !== -1)
  );

const mapStateToProps = (state, ownProps) => {
  const {
    requestedDocuments,
    preparedFormsData,
    requestPages
  } = selectPreparedState(state);

  const partialCompleteInfo =
    isSendRequestActive(state.sendRequestTask) &&
    state.sendRequestTask.tillPage &&
    collectPartialCompleteDocuments(
      state.sendRequestTask.tillPage,
      requestPages,
      requestedDocuments
    );

  return {
    ...ownProps,
    completePageId: isSendRequestActive(state.sendRequestTask)
      ? state.sendRequestTask.tillPage
      : undefined,
    requestedDocuments:
      partialCompleteInfo?.documentsToSend || requestedDocuments,
    completePageIndex: partialCompleteInfo?.completePageIndex,
    forms: preparedFormsData,
    recipientRequestId: state.recipientRequestId,
    successSentKeys: state.successSentKeys,
    errorSentKeys: state.errorSentKeys,
    scannedDocuments: state.scannedDocuments,
    documentsStatus: state.documentsStatus,
    submitted: state.submitted,
    isFetching: state.isFetching,
    caseName: state.caseName,
    companyLogo: state.companyLogo,
    signaturesTouches: state.signaturesTouches,
    canSaveTemplateRequestHistory: state.canSaveTemplateRequestHistory
  };
};

const mapDispatchToProps = dispatch => ({
  setSuccessSentKeys: keys => dispatch(setSuccessSentKeys(keys)),
  setErrorSentKeys: keys => dispatch(setErrorSentKeys(keys))
});

class Sender extends React.Component {
  dialog = null;
  preloader = null;
  lastErrorMessage = '';
  hardErrorOnSend = false;

  dialogStart = () => {
    if (!this.props.noProgressDialog) {
      this.dialog = this.$f7.dialog.progress(II18.get('dialog_sending'), 0);
    }
  };

  preloaderStart = () => {
    if (!this.props.noProgressDialog) {
      this.preloader = this.$f7.dialog.preloader(II18.get('title_loading'));
    }
  };

  preloaderClose = () => {
    if (this.preloader) {
      // Framework7 dialogs are safe to close/destroy even if already closing.
      this.preloader.close && this.preloader.close();
      this.preloader.destroy && this.preloader.destroy();
      this.preloader = null;
    }
  };

  dialogClose = () => {
    // Close both progress dialog and the interim preloader dialog.
    this.preloaderClose();
    this.dialog && this.dialog.close();
  };

  dialogSetText = text => {
    this.dialog && this.dialog.setText(text);
  };

  dialogSetProgress = progress => {
    this.dialog && this.dialog.setProgress(progress);
  };

  logApiError = error => {
    this.lastErrorMessage = getResponseErrorMessage(error);

    gae('SENDER', 'API_ERROR_MESSAGE', this.lastErrorMessage);
    if (this.lastErrorMessage.indexOf('MODEL_NOT_FOUND') !== -1) {
      this.hardErrorOnSend = this.lastErrorMessage;
    }
  };

  processApiResponse = async response => {
    if (response.errorMessage || response.errorCode) {
      if (
        this.isInfoMessageSaysDocumentAlreadySubmitted(response.infoMessage)
      ) {
        gae('SENDER', 'API_ERROR_MESSAGE_FALSE_POSITIVE', this.infoMessage);
        return true;
      }
      this.logApiError(
        `${response.errorCode}: ${response.errorMessage || ''} ${
          response.infoMessage
        }`
      );
      return false;
    }
    return true;
  };

  checkDocumentIsSubmitted = async documentId => {
    let result = false;
    gae('SENDER', 'CHECK_DOCUMENT_STATE', documentId);
    await PaperflyAPI.getDocumentState(
      this.props.recipientRequestId,
      documentId
    ).then(
      response => {
        gae('SENDER', 'DOCUMENT_STATE_IS', response?.state);
        result = response?.state && response.state !== 'PENDING';
      },
      error => this.logApiError(error)
    );
    gae('SENDER', 'DOCUMENT_STATE_IS_SUBMITTED', result);
    return result;
  };

  submitCurrentForm = async (documentId, formDataMap, signatureTouches) => {
    let uploadOk = false;

    const jsonData = esMapToJsonObject(formDataMap);

    await PaperflyAPI.submitRequestedDocumentForm(
      this.props.recipientRequestId,
      documentId,
      jsonData,
      signatureTouches?.id,
      signatureTouches?.data
    ).then(
      response => (uploadOk = this.processApiResponse(response, documentId)),
      error => this.logApiError(error)
    );
    return uploadOk;
  };

  acceptCurrentInfoDoc = async documentId => {
    let uploadOk = false;
    await PaperflyAPI.acceptRequestedDocumentForInformation(
      this.props.recipientRequestId,
      documentId
    ).then(
      response => (uploadOk = this.processApiResponse(response, documentId)),
      error => this.logApiError(error)
    );
    return uploadOk;
  };

  uploadRequestedDocumentPage = async (
    curDocKey,
    curPageKey,
    base64,
    lastPage
  ) => {
    const sourcePdf = this.props.documentsStatus.get(curDocKey) || '';

    let uploadOk = false;
    await PaperflyAPI.uploadRequestedDocumentPage(
      this.props.recipientRequestId,
      curDocKey,
      curPageKey,
      base64,
      sourcePdf,
      lastPage
    ).then(
      response => (uploadOk = this.processApiResponse(response, curDocKey)),
      error => this.logApiError(error)
    );
    return uploadOk;
  };

  uploadIdCheck = async (
    documentId,
    docFrontData,
    docBackData,
    selfieData,
    idCheckDocType
  ) => {
    let uploadOk = false;
    await PaperflyAPI.uploadIdCheck(
      this.props.recipientRequestId,
      documentId,
      docFrontData,
      docBackData,
      selfieData,
      idCheckDocType
    ).then(
      response => (uploadOk = this.processApiResponse(response, documentId)),
      error => this.logApiError(error)
    );
    return uploadOk;
  };

  isInfoMessageSaysRequestAlreadySubmitted = completeResponse => {
    return completeResponse?.infoMessage?.indexOf('found SUBMITTED') > -1;
  };

  isInfoMessageSaysDocumentAlreadySubmitted = message => {
    return SUCCESSFUL_DOCUMENT_STATUSES.some(
      status => message.indexOf('found ' + status) !== -1
    );
  };

  closeRequest = async () => {
    return withSpan(
      'request.complete',
      {
        'complete.kind': this.props.completePageId ? 'step' : 'request',
        'complete.has_page_id': Boolean(this.props.completePageId)
      },
      async () => {
        let closed = false;
        if (this.props.completePageId) {
          gae('SENDER', 'TRY_COMPLETE_PAGE', this.props.completePageId);
          await PaperflyAPI.completeStep(
            this.props.recipientRequestId,
            this.props.completePageId
          ).then(
            () => {
              closed = true;
              gae('SENDER', 'SUCCESSFUL_COMPLETE_STEP');
              if (window.pwaSessionWithErrors) {
                PaperflyAPI.submitErrorProtocol(
                  this.props.recipientRequestId,
                  'SUCCESSFUL_COMPLETE_STEP_AFTER_ERRORS',
                  ''
                );
              }
            },
            error => {
              this.logApiError(error);
            }
          );
        } else {
          gae('SENDER', 'TRY_COMPLETE_REQUEST');
          await PaperflyAPI.completeRequest(this.props.recipientRequestId).then(
            response => {
              if (
                response.submitted ||
                this.isInfoMessageSaysRequestAlreadySubmitted(response)
              ) {
                closed = true;
                gae('SENDER', 'SUCCESSFUL_COMPLETE_REQUEST');
                resetRequestIdCookie();
                if (window.pwaSessionWithErrors) {
                  PaperflyAPI.submitErrorProtocol(
                    this.props.recipientRequestId,
                    'SUCCESSFUL_COMPLETE_REQUEST_AFTER_ERRORS',
                    ''
                  );
                }
              } else {
                gae('SENDER', 'FAIL_COMPLETE_REQUEST');
                this.logApiError({ response });
              }
            },
            error => {
              this.logApiError(error);
            }
          );
        }
        return closed;
      }
    );
  };

  done = async () => {
    gae('SENDER', 'SEND_FILES_DONE');
    const complete = await this.closeRequest();
    this.dialogClose();

    if (!complete) {
      PaperflyAPI.submitErrorProtocol(
        this.props.recipientRequestId,
        'ERROR_ON_COMPLETE_REQUEST',
        this.lastErrorMessage
      );

      this.$f7.dialog.alert(
        `${II18.get(
          'dialog_error_on_send'
        )}<br/><br/><i>FAIL_ON_COMPLETE_REQUEST</i>`,
        II18.get('common_error')
      );
      endJourney(JOURNEY.SUBMIT, 'error', 'complete_failed');
      this.props.onError();
    } else {
      endJourney(JOURNEY.SUBMIT, 'ok', {
        'submit.result': 'success'
      });
      this.props.onDone();
    }
    this.finishSendSession();
  };

  buildUploadErrorDoc = errDocInfo => {
    const invalidImage =
      errDocInfo.invalidImage || isInvalidImageUploadError(errDocInfo.message);
    return {
      ...errDocInfo,
      invalidImage,
      displayMessage:
        errDocInfo.displayMessage ||
        (invalidImage ? II18.get('sender_invalid_image_hint') : undefined)
    };
  };

  doOnError = (errMessage = '') => {
    gae('SENDER', 'ERROR_ON_SEND_FILES');

    let infoText = '';
    let errorText = '';

    const errorSentKeys = new Set();
    const requestedDocuments = this.props.requestedDocuments;

    let requestWrongState = false;
    let invalidImageErrorCount = 0;
    this.errorDocs.forEach(errDocInfo => {
      errorSentKeys.add(errDocInfo.documentId + (errDocInfo.pageId || ''));

      const errDoc = requestedDocuments.find(
        d => d.id === errDocInfo.documentId
      );
      const docTitle =
        errDoc.name +
        (errDocInfo.pageIndex !== undefined
          ? ` (${II18.get('common_page_x', errDocInfo.pageIndex)})`
          : '');
      infoText += ` &#x26A0; ${docTitle}<br/>`;
      if (errDocInfo.displayMessage) {
        infoText += ` - ${errDocInfo.displayMessage}<br/>`;
      }
      errorText += ` - ${docTitle} (${errDoc.type}: ${errDoc.id}): \r\n${errDocInfo.message}\r\n`;

      if (
        errDocInfo.invalidImage ||
        isInvalidImageUploadError(errDocInfo.message)
      ) {
        invalidImageErrorCount++;
      }

      requestWrongState =
        requestWrongState ||
        errDocInfo.message?.indexOf('CLOSED_BEFORE_SUBMIT') !== -1 ||
        (errDocInfo.message?.indexOf('WRONG_REQUEST_STATE') !== -1 &&
          errDocInfo.message?.indexOf('found SUBMITTED') !== -1);
    });

    const allErrorsAreInvalidImage =
      this.errorDocs.size > 0 && invalidImageErrorCount === this.errorDocs.size;

    this.dialogClose();

    this.props.setErrorSentKeys(errorSentKeys);

    gae(
      'SENDER',
      requestWrongState
        ? 'WRONG_REQUEST_STATE_ON_SEND'
        : allErrorsAreInvalidImage
        ? 'ERROR_ON_SEND_INVALID_IMAGE'
        : 'ERROR_ON_SEND_DOCUMENT',
      errMessage + ' | ' + errorText
    );

    PaperflyAPI.submitErrorProtocol(
      this.props.recipientRequestId,
      requestWrongState
        ? 'WRONG_REQUEST_STATE_ON_SEND'
        : allErrorsAreInvalidImage
        ? 'ERROR_ON_SEND_INVALID_IMAGE'
        : 'ERROR_ON_SEND_DOCUMENT',
      errMessage + ' | ' + errorText
    );

    if (requestWrongState) {
      this.$f7.dialog.alert(
        II18.get('sender_cant_send_request') +
          ': <br>' +
          II18.get('home_request_closed'),
        II18.get('common_error')
      );
    } else {
      this.$f7.dialog.alert(
        this.hardErrorOnSend
          ? II18.get('technical_error_hard', this.hardErrorOnSend)
          : allErrorsAreInvalidImage
          ? II18.get('dialog_error_on_send_invalid_image', infoText)
          : II18.get('dialog_error_on_send', infoText),
        II18.get('common_error')
      );
    }
    this.props.onError();
    endJourney(JOURNEY.SUBMIT, 'error', errMessage || 'send_failed');
    this.finishSendSession();
  };

  finishSendSession = () => {
    window.senderIsActive = false;
  };

  async onSendRequest() {
    gae('SENDER', 'START_SEND_FILES');

    // Prepare finished once real upload/submit work begins
    endJourney(JOURNEY.PREPARE, 'ok', { 'prepare.result': 'ready_to_send' });

    return runInJourney(JOURNEY.SUBMIT, async () => {
      const {
        requestedDocuments,
        scannedDocuments,
        forms,
        successSentKeys,
        setSuccessSentKeys,
        //errorSentKeys,
        setErrorSentKeys,
        documentsStatus,
        completePageId,
        completePageIndex
      } = this.props;

      const _successSentKeys = new Set(successSentKeys);
      const uploadDocs = [];
      const formDocs = [];
      const infoDocs = [];
      const idCheckDocs = [];
      this.errorDocs = new Set();

      if (completePageId) {
        gae(
          'SENDER',
          'SEND_TILL_PAGE',
          `${completePageId} index: ${completePageIndex}`
        );
        gae(
          'SENDER',
          'DOCUMENTS_TO_SEND_ONLY',
          JSON.stringify(requestedDocuments.map(doc => doc.name))
        );
      }

      try {
        gae('SENDER', 'DO_BEFORE_SUBMIT');
        // First phase ("checking document states") can be slow:
        // show a simple common preloader immediately (no % progress yet).
        this.preloaderStart();
        let progressSteps = 0;
        for (const documentEntry of requestedDocuments) {
          gae(
            'SENDER',
            'CHECK_DOCUMENT_STATUS_AND_STATE',
            `${documentEntry.type}${
              documentEntry.optional ? ' [OPTIONAL]' : ''
            }${documentEntry.rejected ? ' [REJECTED]' : ''}${
              documentEntry.done ? ' [DONE]' : ' [NOT_DONE]'
            } "${documentEntry.name.slice(0, 10)}..." (${documentEntry.id})`
          );

          if (
            !documentEntry.optional &&
            !documentEntry.done &&
            !documentEntry.rejected
          ) {
            this.errorDocs.add({
              documentId: documentEntry.id,
              message: `ERROR: document status is not DONE (${
                documentEntry.type
              } ${documentEntry.name} ${documentEntry.readyStatus || ''})`,
              displayMessage: II18.get(
                'dialog_required_document_' + documentEntry.type,
                documentEntry.name
              )
            });
          }
          if (await this.checkDocumentIsSubmitted(documentEntry.id)) {
            _successSentKeys.add(documentEntry.id);
          }
          if (
            !isDocumentSubmitted(documentEntry) &&
            !_successSentKeys.has(documentEntry.id)
          ) {
            if (
              documentEntry.type === DOC_TYPE_DOCUMENT &&
              scannedDocuments.get(documentEntry.id)?.size
            ) {
              progressSteps += scannedDocuments.get(documentEntry.id)?.size;
              uploadDocs.push(documentEntry);
            }

            if (
              documentEntry.type === DOC_TYPE_FORM ||
              documentEntry.type === DOC_TYPE_PDF_FORM
            ) {
              formDocs.push(documentEntry);
              progressSteps++;
            }

            if (
              documentEntry.type === DOC_TYPE_INFORMATION ||
              documentEntry.type === DOC_TYPE_IMMEDIATE_CONFIRMATION
            ) {
              infoDocs.push(documentEntry);
              progressSteps++;
            }

            if (
              documentEntry.type === DOC_TYPE_ID_CHECK &&
              scannedDocuments.get(documentEntry.id)?.size
            ) {
              idCheckDocs.push(documentEntry);
              progressSteps++;
            }
          } else {
            _successSentKeys.add(documentEntry.id);
          }
        }

        if (this.errorDocs.size > 0) {
          this.doOnError();
          return;
        }
        // Checks done → switch UI to the progress dialog.
        setJourneyAttributes(JOURNEY.SUBMIT, {
          'submit.upload_doc_count': uploadDocs.length,
          'submit.form_doc_count': formDocs.length,
          'submit.info_doc_count': infoDocs.length,
          'submit.idcheck_doc_count': idCheckDocs.length
        });
        this.preloaderClose();
        let progressStep = 100 / (progressSteps || 1);
        let progress = 0;

        this.dialogStart();

        const errorDocsHas = id => {
          for (let item of this.errorDocs)
            if (item.documentId === id) return true;
        };

        const doneSendFiles = () => {
          setSuccessSentKeys(_successSentKeys);

          if (this.errorDocs.size) {
            this.doOnError();
          } else {
            setErrorSentKeys(new Set());
            this.done();
          }
        };

        const uploadDocumentPage = async (
          documentId,
          curPageKey,
          curPageUrl,
          pageIndex,
          lastPage
        ) => {
          let uploadOk = false;

          gae(
            'SENDER',
            'PREPARE_DOCUMENT_PAGE_URL_TO_BASE64',
            curPageKey,
            pageIndex + (lastPage ? ' lastPage' : '')
          );

          let base64 = await prepareBase64FromUrl(curPageUrl);
          if (base64) {
            gae(
              'SENDER',
              'UPLOAD_DOCUMENT_PAGE',
              curPageKey,
              pageIndex + (lastPage ? ' lastPage' : '')
            );
            uploadOk = await this.uploadRequestedDocumentPage(
              documentId,
              curPageKey,
              base64,
              lastPage
            );
          }
          return uploadOk;
        };

        const prepareBase64FromUrl = async imageUrl => {
          let base64;
          if (imageUrl) {
            await getBase64FromUrl(imageUrl)
              .then(r => (base64 = r))
              .catch(e => {
                gae(
                  'SENDER',
                  'EXCEPTION_ON_PREPARE_BASE64 - ' + e.name + ': ' + e.message
                );
                if (e?.message?.indexOf('FileReader') !== -1) {
                  this.hardErrorOnSend = e.name + ': ' + e.message;
                  gae('SENDER', 'PWA_LAST_ERROR_IS_HARD_ERROR');
                }
              });

            if (!base64) {
              gae('SENDER', 'Error on fetch base64 from url: ' + imageUrl);
              this.lastErrorMessage =
                'Error on fetch base64 from url: ' + imageUrl;
              return null;
            }

            const analysis = analyzeJpegOrPngBase64(base64);
            if (!analysis.valid) {
              const details = [
                analysis.reason,
                analysis.mime && `mime=${analysis.mime}`,
                analysis.byteLength != null && `bytes=${analysis.byteLength}`,
                analysis.headerHex && `header=${analysis.headerHex}`
              ]
                .filter(Boolean)
                .join(', ');
              gae('SENDER', 'INVALID_JPEG_OR_PNG_BASE64', details);
              this.lastErrorMessage =
                'INVALID_JPEG_OR_PNG_BASE64: The given base64 is not a valid jpeg or png base64 String (' +
                details +
                ')';
              return null;
            }
          }
          return base64;
        };

        /**
         * Upload scan documents.
         */
        if (uploadDocs.length > 0) {
          await withSpan(
            'document.upload',
            {
              'document.type': 'DOCUMENT',
              'document.count': uploadDocs.length
            },
            async () => {
              for (const doc of uploadDocs) {
                gae('SENDER', 'UPLOAD_DOCUMENT', doc.id.substring(0, 4));

                const scannedPages = scannedDocuments.get(doc.id);
                let successPagesCount = 0;
                let pageIndex = 1;

                for (let [curPageKey, curPageUrl] of scannedPages) {
                  this.dialogSetText(
                    `${doc.name}: ${II18.get('common_page_x_from_y_2', [
                      pageIndex,
                      scannedPages.size
                    ])}`
                  );

                  const lastPage = scannedPages.size === successPagesCount + 1;

                  if (!_successSentKeys.has(doc.id + curPageKey) || lastPage) {
                    if (
                      await uploadDocumentPage(
                        doc.id,
                        curPageKey,
                        curPageUrl,
                        pageIndex,
                        lastPage
                      )
                    ) {
                      successPagesCount++;
                      _successSentKeys.add(doc.id + curPageKey);
                      gae(
                        'SENDER',
                        'UPLOAD_DOCUMENT_PAGE_SUCCESS',
                        curPageKey,
                        pageIndex
                      );
                    } else {
                      gae(
                        'SENDER',
                        'ERR_ON_UPLOAD_DOCUMENT_PAGE',
                        curPageKey,
                        pageIndex
                      );

                      this.errorDocs.add(
                        this.buildUploadErrorDoc({
                          documentId: doc.id,
                          pageId: curPageKey,
                          pageIndex,
                          message: this.lastErrorMessage
                        })
                      );
                    }
                  } else {
                    successPagesCount++;
                  }

                  progress += progressStep;
                  this.dialogSetProgress(progress);
                  pageIndex++;
                }
                if (!errorDocsHas(doc.id)) {
                  _successSentKeys.add(doc.id);
                }
              }
              if (uploadDocs.length > 0) {
                gae('SENDER', 'UPLOAD_ALL_DOCUMENTS_WITH_PAGES_DONE');
              }
            }
          );
        }

        /**
         * Upload Forms
         */
        if (formDocs.length > 0) {
          await withSpan(
            'document.submit_form',
            {
              'document.count': formDocs.length
            },
            async () => {
              for (const form of formDocs) {
                let curFormKey = form.id;

                gae('SENDER', 'SUBMIT_FORM', curFormKey.substring(0, 4));

                this.dialogSetText(form.name);
                let subProgress = 0;
                const baseProgress = progress;
                const subProgressInterval = setInterval(() => {
                  subProgress += progressStep / 30;
                  this.dialogSetProgress(baseProgress + subProgress);
                }, 1000);

                const uploadOk = await this.submitCurrentForm(
                  curFormKey,
                  forms.get(curFormKey),
                  this.props.signaturesTouches.get(curFormKey)
                );
                if (uploadOk) {
                  _successSentKeys.add(curFormKey);
                  gae('SENDER', 'SUBMIT_FORM_DONE', curFormKey.substring(0, 4));
                } else {
                  //console.log('error on upload form: ' + curFormKey);
                  gae('SENDER', 'ERR_ON_SUBMIT_FORM');
                  this.errorDocs.add({
                    documentId: curFormKey,
                    message: this.lastErrorMessage
                  });
                }

                clearInterval(subProgressInterval);
                progress += progressStep;
                this.dialogSetProgress(progress);
              }
              if (formDocs.length > 0) {
                gae('SENDER', 'UPLOAD_ALL_FORMS_DONE');
              }
            }
          );
        }

        /**
         * Upload Infos
         */
        if (infoDocs.length > 0) {
          await withSpan(
            'document.accept_info',
            { 'document.count': infoDocs.length },
            async () => {
              for (let doc of infoDocs) {
                const infoDocumentKey = doc.id;
                gae(
                  'SENDER',
                  'ACCEPT_DOCUMENT',
                  infoDocumentKey.substring(0, 4)
                );

                this.dialogSetText(doc.name);
                const uploadOk = await this.acceptCurrentInfoDoc(
                  infoDocumentKey
                );

                if (uploadOk) {
                  _successSentKeys.add(infoDocumentKey);
                } else {
                  gae('SENDER', 'ERR_ON_ACCEPT_DOCUMENT');

                  this.errorDocs.add({
                    documentId: infoDocumentKey,
                    message: this.lastErrorMessage
                  });
                }
                progress += progressStep;
                this.dialogSetProgress(progress);
              }
              if (infoDocs.length > 0) {
                gae('SENDER', 'UPLOAD_ALL_INFO_DONE');
              }
            }
          );
        }

        /**
         * Upload IdChecks
         */
        if (idCheckDocs.length > 0) {
          await withSpan(
            'idcheck.upload',
            { 'document.count': idCheckDocs.length },
            async () => {
              for (let doc of idCheckDocs) {
                const idCheckDocumentKey = doc.id;
                gae(
                  'SENDER',
                  'IDCHECK_DOCUMENT',
                  idCheckDocumentKey.substring(0, 4)
                );

                this.dialogSetText(doc.name);
                const idCheckDocumentType = defineIdCheckDocumentTypeFromStatus(
                  doc,
                  documentsStatus.get(idCheckDocumentKey)
                );
                const scannedPages = scannedDocuments.get(doc.id);
                const uploadOk = await this.uploadIdCheck(
                  idCheckDocumentKey,
                  await prepareBase64FromUrl(
                    scannedPages.get(ID_CHECK_STEP_DOC_FRONT)
                  ),
                  await prepareBase64FromUrl(
                    scannedPages.get(ID_CHECK_STEP_DOC_BACK)
                  ),
                  await prepareBase64FromUrl(
                    scannedPages.get(ID_CHECK_STEP_DOC_SELFIE)
                  ),
                  idCheckDocumentType
                );

                if (uploadOk) {
                  _successSentKeys.add(idCheckDocumentKey);
                } else {
                  gae('SENDER', 'ERR_ON_ID_CHECK_DOCUMENT');

                  this.errorDocs.add(
                    this.buildUploadErrorDoc({
                      documentId: idCheckDocumentKey,
                      message: this.lastErrorMessage
                    })
                  );
                }
                progress += progressStep;
                this.dialogSetProgress(progress);
              }
              if (idCheckDocs.length > 0) {
                gae('SENDER', 'UPLOAD_ALL_ID_CHECK_DONE');
              }
            }
          );
        }

        doneSendFiles();
      } catch (e) {
        gaeError('CRASH_ON_SEND_FILES - ' + e.name + ': ' + e.message);
        this.doOnError(e.stack);
      }
    });
  }

  componentDidMount() {
    if (!window.senderIsActive) {
      gae('SENDER', 'IS_MOUNTED_AND_SET_AS_ACTIVE');
      window.senderIsActive = true;
      this.onSendRequest();
    }
  }

  componentWillUnmount() {}

  render = () => {
    return null;
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Sender);

Sender.propTypes = {
  forms: PropTypes.object,
  signaturesTouches: PropTypes.object,
  onDone: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  setSuccessSentKeys: PropTypes.func,
  setErrorSentKeys: PropTypes.func,
  recipientRequestId: PropTypes.string.isRequired,
  requestedDocuments: PropTypes.array,
  completePageIndex: PropTypes.number,
  scannedDocuments: PropTypes.object,
  successSentKeys: PropTypes.object,
  errorSentKeys: PropTypes.object,
  documentsStatus: PropTypes.object,
  submitted: PropTypes.bool,
  isFetching: PropTypes.bool,
  noProgressDialog: PropTypes.bool,
  caseName: PropTypes.string,
  companyLogo: PropTypes.string,
  completePageId: PropTypes.string
};
